import { bool, number, object } from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import {
  AddressMain,
  CountryField,
  FnrAddress,
  Line1,
  Line2,
} from "UI/FnrAddress";
import {
  AddressAutocompleteRoot,
  AddressCallouts,
  AutocompleteCityField,
  AutocompleteStateField,
  AutocompleteStreetField,
  AutocompleteZipField,
  CountryFlagCorner,
  useAddressAutocomplete,
} from "UI/FnrAddressAutocomplete";

import { countryCodeFromIDOrCode } from "Utilities/Address/networkCountryOptions";

import addressToBulkFieldData from "./addressToBulkFieldData";

// Subfields whose edits mark the address dirty (and re-engage autocomplete).
const DIRTY_SUBFIELDS = ["addr1", "city", "state", "zip"];

// The legacy shared Save button on the Simple Signup / completeMyProfile page.
const SHARED_SAVE_SELECTOR = ".button_save_extended";

SignupAddressCard.propTypes = {
  // The live `window.data.extendedFields[i]` entry for this Address field. We
  // set its `editFormObj` so the legacy shared Save's bulk loop
  // (signupExtendedFields.js#saveExtendedFields) collects our value.
  efInfo: object.isRequired,
  networkFieldID: number.isRequired,
  // The saved `response` from the bootstrap (may be empty/undefined).
  initialValue: object,
  countryVisible: bool,
};

/**
 * React-owned Address field for the Simple Signup / completeMyProfile page.
 *
 * Unlike IIG's single-field card, this surface does NOT own its Save button —
 * one shared Save at the bottom collects every field and POSTs them together
 * to /user/saveExtendedFieldResponsesBulk, then navigates to the confirm page.
 * So this card renders only the field (no Save button, no cross-network picker,
 * no POST of its own) and feeds its value into that shared save by installing
 * `efInfo.editFormObj.getPostData` — the exact seam the bulk loop reads.
 *
 * It is a collapse-in-place / no-hold surface (the save navigates away, so a
 * post-save standardization callout would have no home): the autocomplete hook
 * writes committed values straight into local state, callouts are live, and
 * `verifyDeliverability` runs the backend USPS check so the USPS-Verified badge
 * only lights on a confirmed-deliverable address.
 */
export default function SignupAddressCard({
  efInfo,
  networkFieldID,
  initialValue,
  countryVisible,
}) {
  const [value, setValue] = useState(() => ({
    addr1: initialValue?.addr1 || "",
    city: initialValue?.city || "",
    state: initialValue?.state || "",
    zip: initialValue?.zip || "",
    plus4: initialValue?.plus4 || "",
    // The hook speaks ISO and gates US-only verification on `country === "US"`;
    // the stored value may be an ISO code or a numeric ID, so normalize.
    country: countryCodeFromIDOrCode(initialValue?.country),
    isVerified: initialValue?.isVerified === true,
    // Read the provider/verifiedAt back so `uspsVerified` (gated on
    // verificationProvider === "smarty") can light the badge, and so the hook
    // can round-trip them on re-verify.
    verificationProvider: initialValue?.verificationProvider || "",
    verifiedAt: initialValue?.verifiedAt || "",
    validationSkipped: initialValue?.validationSkipped || "",
  }));

  const initialRef = useRef(value);
  const addressIsDirty = DIRTY_SUBFIELDS.some(
    (f) => (value[f] || "") !== (initialRef.current[f] || "")
  );

  // A ref mirror of the value, kept fresh synchronously. The shared Save reads
  // our value via `getPostData` at click time; a live forward-ghost committed
  // by the button-click blur lands on the NEXT render, after the bulk loop has
  // already run. Reading (and freezing, below) the ref lets the POST carry the
  // completion the user was looking at rather than the pre-commit fragment.
  const valueRef = useRef(value);
  valueRef.current = value;

  const onChange = useCallback((delta) => {
    valueRef.current = { ...valueRef.current, ...delta };
    setValue((prev) => ({ ...prev, ...delta }));
  }, []);

  const containerRef = useRef(null);
  const streetRef = useRef(null);
  const cityRef = useRef(null);
  const zipRef = useRef(null);

  const autocomplete = useAddressAutocomplete({
    value,
    onChange,
    addr1Ref: streetRef,
    cityRef,
    zipRef,
    containerRef,
    isDirty: addressIsDirty,
    verifyDeliverability: true,
  });
  const {
    liveUnverifiable,
    isUnverifiable,
    standardization,
    onUndoStandardization,
    commitVisibleGhosts,
  } = autocomplete;

  // Feed the shared bulk save. The legacy loop reads `efInfo.editFormObj
  // .getPostData()` for every field; standing in for the legacy address widget,
  // we return the serialized hook value (incl. the verification verdict) keyed
  // by networkFieldID. Reading `valueRef` (not a captured `value`) keeps it
  // current without reinstalling the shim on every keystroke.
  useEffect(() => {
    efInfo.editFormObj = {
      getPostData: () => addressToBulkFieldData(valueRef.current, networkFieldID),
    };
    return () => {
      // Only retract our own shim, never a foreign one.
      if (efInfo.editFormObj?.getPostData) delete efInfo.editFormObj;
    };
  }, [efInfo, networkFieldID]);

  // Clicking the shared Save first blurs the street input, which commits a live
  // ghost via onChange — but that setValue lands next render, after the bulk
  // loop has read getPostData. Freeze the visible ghost synchronously on the
  // capture phase (before the legacy click handler runs) so the POST carries
  // what the user saw. onChange updates valueRef synchronously, so getPostData
  // reads the frozen value.
  useEffect(() => {
    const saveButton = document.querySelector(SHARED_SAVE_SELECTOR);
    if (!saveButton) return undefined;
    const freeze = () => commitVisibleGhosts();
    saveButton.addEventListener("click", freeze, true);
    return () => saveButton.removeEventListener("click", freeze, true);
  }, [commitVisibleGhosts]);

  return (
    <Wrapper>
      <AddressAutocompleteRoot ref={containerRef} value={autocomplete}>
        <FnrAddress>
          <AddressMain>
            <StyledLine1 $reserveFlag={countryVisible}>
              <AutocompleteStreetField
                inputStyle={streetStyle}
                acceptRightOffset={countryVisible ? 44 : undefined}
              />
              {countryVisible && (
                <CountryFlagCorner>
                  <CountryField
                    value={value.country}
                    onChange={(id) =>
                      onChange({ country: countryCodeFromIDOrCode(id) })
                    }
                    label=""
                  />
                </CountryFlagCorner>
              )}
            </StyledLine1>
            <Line2>
              <AutocompleteCityField />
              <AutocompleteStateField countryIDOrCode={value.country} />
              <ZipWrapper>
                <AutocompleteZipField countryID={value.country} />
              </ZipWrapper>
            </Line2>
          </AddressMain>
        </FnrAddress>
      </AddressAutocompleteRoot>

      <AddressCallouts
        standardizationOpen={Boolean(standardization)}
        unverifiableOpen={liveUnverifiable || isUnverifiable}
        onUndo={onUndoStandardization}
      />
    </Wrapper>
  );
}

// Match the legacy signup fields' box chrome (forms.css) so the address group
// sits in the same bordered card as its sibling Text/Select/Number inputs: a
// 1px #98a4a5 border at 7px radius that darkens on hover and turns focus-blue
// while any subfield is focused. The FnrAddress kit ships borderless (its other
// surfaces supply their own framing), so the border lives here, not in the kit.
const Wrapper = styled.div`
  [data-cy="fnrAddress"] {
    border: 1px solid #98a4a5;
    border-radius: 7px;
    transition: border-color 0.15s ease;
  }
  [data-cy="fnrAddress"]:hover {
    border-color: #828c8d;
  }
  [data-cy="fnrAddress"]:focus-within {
    border-color: #278dc1;
  }
  /* Match the legacy signup inputs' text style: Open Sans 300 at 28px line
     height (the kit ships 400/normal, which reads heavier than the siblings). */
  [data-cy="fnrAddress"] input {
    font-weight: 300;
    line-height: 28px;
  }
  /* Match the legacy signup inputs' placeholder shade (forms.css uses #828c8d);
     the kit ships a darker #757575, which reads heavier than the siblings. */
  [data-cy="fnrAddress"] input::placeholder {
    color: #828c8d;
    opacity: 1;
  }
`;

// Line1's 6px padding moves onto the street field wrapper so the nested country
// flag can sit inside the input's right corner; the corner reservation lives on
// the INPUT itself (StyledLine1's input rule below).
const streetStyle = { padding: "6px" };

// Reserve the 36px right gutter on the input only when the country flag nests
// there, so a country-hidden field doesn't carry dead space on its right.
const StyledLine1 = styled(Line1)`
  position: relative;
  padding: 0;
  input {
    padding-right: ${(p) => (p.$reserveFlag ? "36px" : "6px")};
  }
`;

const ZipWrapper = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 80px;
`;
